import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BaseModal from '../utils/BaseModal/BaseModal';
import Spinner from '../../Spinner/Spinner';
import { REPORT_REASON } from '../ReportMessageModal/ReportMessageModal';
import { fetchReviewById } from '../../../store/slices/chatter/asyncThunks';
import {
  selectChatterActionsPending,
  selectSelectedReview,
  setSelectedReview,
} from '../../../store/slices/chatter/slice';
import { notifyError } from '../../../utils/notify';
import {
  Container,
  EmptyText,
  GoToChatButton,
  InteractionBubble,
  InteractionMeta,
  InteractionText,
  MetaGrid,
  MetaItem,
  MetaLabel,
  MetaValue,
  Section,
  SectionLabel,
  SectionValue,
  SeenBadge,
  SpinnerWrapper,
} from './ReviewDetailsModal.styles';

const REASON_LABELS = {
  [REPORT_REASON.NOT_USING_AI_CHAT_AFTER_CONVERT]: 'Not using AI chat after they convert',
  [REPORT_REASON.NOT_USING_AI_CHAT_AFTER_LOCKED_PVV]: 'Not using AI chat after they send a locked PVV and fan objects',
  [REPORT_REASON.NOT_FOLLOWING_SEXTING_SET_ORDER]: 'Not following sexting set order',
  [REPORT_REASON.LADDERING_FROM_MULTIPLE_SEXTING_SETS]: 'Laddering from multiple sexting sets',
  [REPORT_REASON.BURNING_SEXTING_SETS]: 'Burning sexting sets',
  [REPORT_REASON.BREAKING_LADDER_PRICE]: 'Breaking the ladder price without fan asking for it',
  [REPORT_REASON.NOT_HANDLING_HIGH_PRICE_OBJECTIONS]: 'Not handling a high price objections by lowering price',
  [REPORT_REASON.DRAINING_SAME_GUY_TWICE]: 'Trying to drain the same guy twice a day after a big spend',
  [REPORT_REASON.OTHER]: 'Other',
};

const getDisplayName = value => {
  return value.name || '-';
};

const getInteractionText = interaction => {
  if (!interaction) return null;
  return interaction.message || null;
};

const ReviewDetailsModal = ({ modalRef, reviewId, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const review = useSelector(selectSelectedReview);
  const { fetchReviewById: fetchReviewByIdPending } = useSelector(selectChatterActionsPending);

  useEffect(() => {
    if (!reviewId) return;
    dispatch(fetchReviewById({ reviewId }))
      .unwrap()
      .catch(err => {
        notifyError(err.message);
      });
  }, [dispatch, reviewId]);

  const handleClose = () => {
    modalRef.current.hide();
  };

  const handleAfterClose = () => {
    dispatch(setSelectedReview(null));
    onClose?.();
  };

  const reasonLabel = review?.reason ? REASON_LABELS[review.reason] || review.reason : null;
  const interactionText = getInteractionText(review?.interaction);

  const handleGoToChat = () => {
    const { id: interactionId, influencerId, userInfluencerId, timestamp } = review.interaction;
    const params = new URLSearchParams();
    if (timestamp) params.append('timestamp', new Date(timestamp).toISOString());
    if (interactionId) params.append('interactionId', interactionId);
    const search = params.toString();
    navigate(`/${influencerId}/messages/chat/${userInfluencerId}${search ? `?${search}` : ''}`);
    modalRef.current.hide();
  };

  return (
    <BaseModal
      title="Review details"
      ref={modalRef}
      increasedWidth
      onClose={handleClose}
      onAfterClose={handleAfterClose}>
      {fetchReviewByIdPending && !review ? (
        <SpinnerWrapper>
          <Spinner width={80} height={80} />
        </SpinnerWrapper>
      ) : !review ? (
        <EmptyText>No review data available.</EmptyText>
      ) : (
        <Container>
          <MetaGrid>
            <MetaItem>
              <MetaLabel>Chatter</MetaLabel>
              <MetaValue>{getDisplayName(review.chatter)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Reviewer</MetaLabel>
              <MetaValue>{getDisplayName(review.reviewer)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Reported on</MetaLabel>
              <MetaValue>{review.createdAt ? format(new Date(review.createdAt), 'd.MM.yyyy HH:mm') : '-'}</MetaValue>
            </MetaItem>
          </MetaGrid>

          <MetaItem>
            <MetaLabel>Status</MetaLabel>
            <div>
              <SeenBadge $seen={review.seen}>{review.seen ? 'Seen' : 'New'}</SeenBadge>
            </div>
          </MetaItem>

          <Section>
            <SectionLabel>Reason</SectionLabel>
            <SectionValue>{reasonLabel || '-'}</SectionValue>
          </Section>

          {review.description && (
            <Section>
              <SectionLabel>Description</SectionLabel>
              <SectionValue>{review.description}</SectionValue>
            </Section>
          )}

          <Section>
            <SectionLabel>Reported message</SectionLabel>
            {interactionText ? (
              <InteractionBubble>
                <InteractionText>{interactionText}</InteractionText>
                {review.interaction?.timestamp && (
                  <InteractionMeta>{format(new Date(review.interaction.timestamp), 'd.MM.yyyy HH:mm')}</InteractionMeta>
                )}
              </InteractionBubble>
            ) : (
              <SectionValue>Message content unavailable.</SectionValue>
            )}
          </Section>

          {interactionText && <GoToChatButton type="button" title="Go to chat" onClick={handleGoToChat} />}
        </Container>
      )}
    </BaseModal>
  );
};

export default ReviewDetailsModal;
